import emailjs from '@emailjs/browser';

function getConfig() {
  const get = (v) => (typeof v === 'string' ? v.trim() : v) || '';
  return {
    serviceId: get(process.env.REACT_APP_EMAILJS_SERVICE_ID),
    templateId: get(process.env.REACT_APP_EMAILJS_TEMPLATE_ID),
    onboardingTemplateId: get(process.env.REACT_APP_EMAILJS_ONBOARDING_TEMPLATE_ID),
    publicKey: get(process.env.REACT_APP_EMAILJS_PUBLIC_KEY),
    adminEmail: get(process.env.REACT_APP_NOTIFY_ADMIN_EMAIL),
  };
}

function isConfigured() {
  const c = getConfig();
  return !!(c.serviceId && c.templateId && c.publicKey && c.adminEmail);
}

function logConfigStatus() {
  const c = getConfig();
  const missing = [];
  if (!c.serviceId) missing.push('REACT_APP_EMAILJS_SERVICE_ID');
  if (!c.templateId) missing.push('REACT_APP_EMAILJS_TEMPLATE_ID');
  if (!c.publicKey) missing.push('REACT_APP_EMAILJS_PUBLIC_KEY');
  if (!c.adminEmail) missing.push('REACT_APP_NOTIFY_ADMIN_EMAIL');
  if (missing.length) console.warn('[emailJsPortal] Not configured – missing:', missing.join(', '));
}

export async function sendPortalEmail({ to_email, subject, message }) {
  const { serviceId, templateId, publicKey } = getConfig();
  if (!serviceId || !templateId || !publicKey) {
    console.warn('[emailJsPortal] Missing EmailJS env vars');
    return { ok: false, skipped: true };
  }
  if (!to_email?.trim()) return { ok: false, error: 'Missing to_email' };
  try {
    await emailjs.send(serviceId, templateId, { to_email, subject, message }, publicKey);
    return { ok: true };
  } catch (err) {
    const msg = err?.text || err?.message || String(err);
    console.warn('[emailJsPortal] send failed:', msg);
    return { ok: false, error: msg };
  }
}

export async function sendAdminNotification({ subject, message, clientEmail, clientSubject, clientMessage }) {
  if (!isConfigured()) {
    logConfigStatus();
    return { ok: false, skipped: true };
  }
  const { adminEmail } = getConfig();
  const adminResult = await sendPortalEmail({ to_email: adminEmail, subject, message });
  if (!adminResult.ok && !adminResult.skipped) return adminResult;
  if (clientEmail?.trim() && clientSubject && clientMessage) {
    await sendPortalEmail({ to_email: clientEmail.trim(), subject: clientSubject, message: clientMessage });
  }
  return adminResult;
}

/**
 * Send password reset email via EmailJS.
 * Template must accept: email (recipient address), link (the Supabase recovery URL).
 */
export async function sendPasswordResetEmail({ to_email, link }) {
  const { serviceId, publicKey } = getConfig();
  const passwordResetTemplateId = (process.env.REACT_APP_EMAILJS_PASSWORD_RESET_TEMPLATE_ID || '').trim();
  const missing = [];
  if (!serviceId) missing.push('REACT_APP_EMAILJS_SERVICE_ID');
  if (!passwordResetTemplateId) missing.push('REACT_APP_EMAILJS_PASSWORD_RESET_TEMPLATE_ID');
  if (!publicKey) missing.push('REACT_APP_EMAILJS_PUBLIC_KEY');
  if (missing.length) {
    console.warn('[emailJsPortal] Password reset email skipped – missing:', missing.join(', '));
    return { ok: false, skipped: true, missing };
  }
  if (!to_email?.trim()) return { ok: false, error: 'Missing to_email' };
  if (!link?.trim()) return { ok: false, error: 'Missing link' };
  try {
    await emailjs.send(serviceId, passwordResetTemplateId, {
      email: to_email.trim(),
      link: link.trim(),
    }, publicKey);
    return { ok: true };
  } catch (err) {
    const msg = err?.text || err?.message || String(err);
    console.warn('[emailJsPortal] sendPasswordResetEmail failed:', msg);
    return { ok: false, error: msg };
  }
}

/**
 * Send client onboarding / invite email via EmailJS.
 * Template must accept: signup_url (and optionally to_email if your template uses it for the recipient).
 * Only used when admin invites a CLIENT (not stakeholder) and provides an email.
 */
export async function sendClientOnboardingEmail({ to_email, signup_url }) {
  const { serviceId, onboardingTemplateId, publicKey } = getConfig();
  const missing = [];
  if (!serviceId) missing.push('REACT_APP_EMAILJS_SERVICE_ID');
  if (!onboardingTemplateId) missing.push('REACT_APP_EMAILJS_ONBOARDING_TEMPLATE_ID');
  if (!publicKey) missing.push('REACT_APP_EMAILJS_PUBLIC_KEY');
  if (missing.length) {
    console.warn('[emailJsPortal] Onboarding email skipped – missing or empty:', missing.join(', '), '– check .env in marketvendr-dashboard and restart (npm start)');
    return { ok: false, skipped: true, missing };
  }
  if (!to_email?.trim()) return { ok: false, error: 'Missing to_email' };
  if (!signup_url?.trim()) return { ok: false, error: 'Missing signup_url' };
  const cleanSignupUrl = String(signup_url).trim().replace(/`/g, '');
  try {
    await emailjs.send(serviceId, onboardingTemplateId, {
      to_email: to_email.trim(),
      signup_url: cleanSignupUrl,
    }, publicKey);
    return { ok: true };
  } catch (err) {
    const msg = err?.text || err?.message || String(err);
    console.warn('[emailJsPortal] sendClientOnboardingEmail failed:', msg);
    return { ok: false, error: msg };
  }
}
